/**
 * Галерея демонстрационных запросов — «что умеет песочница».
 * Показывается в модалке при клике по кнопке «📖 Примеры».
 * Все запросы работают на встроенной демо-схеме `mini-erp`.
 */

export interface QueryExample {
  /** Человекочитаемое название. */
  title: string;
  /** Что запрос показывает — короткое пояснение. */
  hint: string;
  /** Сам текст запроса. */
  source: string;
}

export interface ExampleCategory {
  title: string;
  items: QueryExample[];
}

export const EXAMPLES: ExampleCategory[] = [
  {
    title: 'Основы',
    items: [
      {
        title: 'ВЫБРАТЬ * — вся таблица',
        hint: 'Звёздочка раскрывается во все поля источника.',
        source: `ВЫБРАТЬ *
ИЗ Справочник.Номенклатура`,
      },
      {
        title: 'Только нужные поля',
        hint: 'Указываем поля явно — так делают в проде.',
        source: `ВЫБРАТЬ Код, Наименование
ИЗ Справочник.Склады`,
      },
      {
        title: 'Алиас поля через КАК',
        hint: 'Переименовываем столбец в результате.',
        source: `ВЫБРАТЬ Наименование КАК Имя
ИЗ Справочник.Контрагенты`,
      },
      {
        title: 'ПЕРВЫЕ N — ограничить выборку',
        hint: 'Первые 3 записи из справочника.',
        source: `ВЫБРАТЬ ПЕРВЫЕ 3
    Наименование
ИЗ Справочник.Номенклатура`,
      },
      {
        title: 'РАЗЛИЧНЫЕ — убрать дубли',
        hint: 'Уникальные склады из движений регистра.',
        source: `ВЫБРАТЬ РАЗЛИЧНЫЕ Склад
ИЗ РегистрНакопления.ОстаткиТоваров`,
      },
    ],
  },
  {
    title: 'Условия и сортировка',
    items: [
      {
        title: 'ГДЕ — фильтр по булеву',
        hint: 'Только непомеченные на удаление.',
        source: `ВЫБРАТЬ Наименование
ИЗ Справочник.Номенклатура
ГДЕ ПометкаУдаления = ЛОЖЬ`,
      },
      {
        title: 'ГДЕ — И / ИЛИ / скобки',
        hint: 'Сложное условие с приоритетами.',
        source: `ВЫБРАТЬ Код, Наименование
ИЗ Справочник.Номенклатура
ГДЕ ПометкаУдаления = ЛОЖЬ
    И (Код = "000000003" ИЛИ Код = "000000004")`,
      },
      {
        title: 'УПОРЯДОЧИТЬ ПО … УБЫВ',
        hint: 'Сортировка по имени в обратном порядке.',
        source: `ВЫБРАТЬ Наименование
ИЗ Справочник.Склады
УПОРЯДОЧИТЬ ПО Наименование УБЫВ`,
      },
    ],
  },
  {
    title: 'Агрегаты и группировка',
    items: [
      {
        title: 'КОЛИЧЕСТВО(*)',
        hint: 'Сколько всего позиций в номенклатуре.',
        source: `ВЫБРАТЬ КОЛИЧЕСТВО(*) КАК Всего
ИЗ Справочник.Номенклатура`,
      },
      {
        title: 'СУММА по регистру',
        hint: 'Итог по всем движениям — пока без виртуальных таблиц.',
        source: `ВЫБРАТЬ СУММА(Количество) КАК ВсегоШтук
ИЗ РегистрНакопления.ОстаткиТоваров`,
      },
      {
        title: 'СГРУППИРОВАТЬ ПО',
        hint: 'Сумма по каждому складу.',
        source: `ВЫБРАТЬ
    Склад,
    СУММА(Количество) КАК Итог
ИЗ РегистрНакопления.ОстаткиТоваров
СГРУППИРОВАТЬ ПО Склад
УПОРЯДОЧИТЬ ПО Итог УБЫВ`,
      },
    ],
  },
  {
    title: 'Виртуальные таблицы регистров',
    items: [
      {
        title: 'Остатки на текущий момент',
        hint: 'Без параметра — все движения зачтены.',
        source: `ВЫБРАТЬ
    Склад,
    Номенклатура,
    КоличествоОстаток,
    СуммаОстаток
ИЗ РегистрНакопления.ОстаткиТоваров.Остатки()`,
      },
      {
        title: 'Остатки на дату',
        hint: 'Только движения до указанной даты.',
        source: `ВЫБРАТЬ
    Склад,
    Номенклатура,
    КоличествоОстаток
ИЗ РегистрНакопления.ОстаткиТоваров.Остатки(ДАТАВРЕМЯ(2024, 2, 1))`,
      },
      {
        title: 'Обороты за период',
        hint: 'Приход, Расход и Оборот за интервал.',
        source: `ВЫБРАТЬ
    Номенклатура,
    КоличествоПриход,
    КоличествоРасход,
    КоличествоОборот
ИЗ РегистрНакопления.ОстаткиТоваров.Обороты(
    ДАТАВРЕМЯ(2024, 1, 1),
    ДАТАВРЕМЯ(2024, 4, 1)
)
ГДЕ Склад = "s_main"
УПОРЯДОЧИТЬ ПО КоличествоОборот УБЫВ`,
      },
      {
        title: 'ОстаткиИОбороты за период',
        hint: 'Начальный + Приход/Расход + Конечный в одной строке.',
        source: `ВЫБРАТЬ
    Номенклатура,
    КоличествоНачальныйОстаток,
    КоличествоПриход,
    КоличествоРасход,
    КоличествоКонечныйОстаток
ИЗ РегистрНакопления.ОстаткиТоваров.ОстаткиИОбороты(
    ДАТАВРЕМЯ(2024, 2, 1),
    ДАТАВРЕМЯ(2024, 3, 1)
)
ГДЕ Склад = "s_main"`,
      },
      {
        title: 'Остатки + справочник Номенклатура',
        hint: 'Разыменование измерения через ЛЕВОЕ СОЕДИНЕНИЕ.',
        source: `ВЫБРАТЬ
    Н.Наименование КАК Товар,
    О.КоличествоОстаток КАК Кол
ИЗ РегистрНакопления.ОстаткиТоваров.Остатки() КАК О
ЛЕВОЕ СОЕДИНЕНИЕ Справочник.Номенклатура КАК Н
    ПО О.Номенклатура = Н.Ссылка
УПОРЯДОЧИТЬ ПО Кол УБЫВ`,
      },
    ],
  },
  {
    title: 'Соединения',
    items: [
      {
        title: 'ЛЕВОЕ СОЕДИНЕНИЕ — подтянуть справочник',
        hint: 'Документы + наименование склада.',
        source: `ВЫБРАТЬ
    Н.Номер,
    Н.Дата,
    С.Наименование КАК Склад
ИЗ Документ.РасходнаяНакладная КАК Н
ЛЕВОЕ СОЕДИНЕНИЕ Справочник.Склады КАК С
    ПО Н.Склад = С.Ссылка`,
      },
      {
        title: 'Т.* — все поля одного источника',
        hint: 'Все реквизиты склада из соединения.',
        source: `ВЫБРАТЬ С.*
ИЗ Документ.РасходнаяНакладная КАК Н
ЛЕВОЕ СОЕДИНЕНИЕ Справочник.Склады КАК С
    ПО Н.Склад = С.Ссылка`,
      },
    ],
  },
];
